import React, {
    RefObject,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { Soundfont } from "smplr";
import { useSettings } from "../../utils/settings";
import {
    NOTES,
    getTransposeByFromMode,
    getTransposeByFromToneObject,
    transposeNote,
} from "../eb-chant/note-utils";
import { LIBER_TONES } from "../tones";
import "./Player.css";

const w: any = window;

var AudioContext =
    w.AudioContext || // Default
    w.webkitAudioContext; // Safari and old versions of Chrome

const NOTE_LENGTH = 0.5;

let playingId = 0;

const playTone = (
    ac: AudioContext,
    clef: string,
    clefOffset: number,
    score: string[],
    onFinished: () => void,
    onSetHighlightCount: (count: number) => void,
    transposedBy: number,
    playingIdRef: RefObject<number>
) => {
    const offset = clef === "c" ? 9 - clefOffset : 5 - clefOffset;

    playingId++;
    const thisPlayingId = playingId;

    let t = 0.5;

    const flute = new Soundfont(new AudioContext(), { instrument: "flute" });

    let highlightCount = 0;
    const addHighlight = (noteIndex: number) => {
        setTimeout(() => {
            if ((playingIdRef.current ?? 9999) <= thisPlayingId) {
                highlightCount++;

                onSetHighlightCount(highlightCount);
            } else {
                onSetHighlightCount(0);
            }
        }, t * 1000);
    };

    const scheduleNote = (
        note: string,
        scheduleTime: number,
        long: boolean
    ) => {
        setTimeout(() => {
            if ((playingIdRef.current ?? 9999) <= thisPlayingId) {
                try {
                    flute.start({
                        note: transposeNote(note, transposedBy),
                        duration: long ? NOTE_LENGTH * 2.1 : NOTE_LENGTH * 1.1,
                    });
                } catch {
                    console.log("Couldn't play", note);
                }
            } else {
                onFinished();
            }
        }, scheduleTime * 1000);
    };

    score.forEach((note, noteIndex) => {
        if (note === "6b" || note === "4b" || note === "8b") {
            scheduleNote("Bb3", t, false);

            addHighlight(noteIndex);

            t += NOTE_LENGTH;
        } else if (note === "6b." || note === "4b.") {
            scheduleNote("Bb3", t, true);

            addHighlight(noteIndex);

            t += NOTE_LENGTH * 2;
        } else if (note === ",") {
            t += NOTE_LENGTH;
        } else if (note.length === 2 && note.charAt(1) === ".") {
            const noteOffset = parseInt(note.charAt(0));

            scheduleNote(NOTES[offset + noteOffset], t, true);

            addHighlight(noteIndex);

            t += NOTE_LENGTH * 2;
        } else {
            const noteOffset = parseInt(note);

            scheduleNote(NOTES[offset + noteOffset], t, false);

            addHighlight(noteIndex);

            t += NOTE_LENGTH;
        }
    });
    setTimeout(() => {
        if ((playingIdRef.current ?? 9999) <= thisPlayingId) {
            onFinished();
            onSetHighlightCount(0);
        }
    }, t * 1000);

    return playingId;
};

const getNotesFromGabc = (gabc: string, onlyIntone: boolean) => {
    let isInsideNeume = false;
    let clefLetter = "";
    let clefOffset = -1;

    const hasStar = gabc.indexOf("*") !== -1;

    const score: string[] = [];

    let foundStar = false;

    const flattened = new Set<string>([]);

    const pushNote = (num: string) => {
        if (flattened.has(num)) {
            score.push(`${num}b`);
        } else {
            score.push(num);
        }
    };

    for (let charIndex = 0; charIndex < gabc.length; charIndex++) {
        if (
            onlyIntone &&
            ((hasStar && foundStar) || (score.length >= 5 && !isInsideNeume))
        ) {
            const finalNote = score.pop()?.replace(".", "");

            while (score[score.length - 1] === finalNote) {
                score.pop();
            }

            score.push(`${finalNote}.`);
            score.push(`${score[0]}.`);
            break;
        }

        const char = gabc[charIndex];

        if (char === ")") {
            isInsideNeume = false;
        }

        const lcChar = char.toLocaleLowerCase();

        if (isInsideNeume) {
            if (clefLetter === "") {
                clefLetter = lcChar;
                continue;
            }

            if (clefOffset === -1) {
                clefOffset = parseInt(lcChar) * 2 - 1;
            }

            if (lcChar === "x") {
                flattened.add(score[score.length - 1]);
                score.pop();
            }

            if ("abcdefghijkl".includes(lcChar)) {
                pushNote(
                    [
                        //"-3",
                        "-2",
                        "-1",
                        "0",
                        "1",
                        "2",
                        "3",
                        "4",
                        "5",
                        "6",
                        "7",
                        "8",
                        "9",
                    ]["abcdefghijkl".indexOf(lcChar)]
                );
            }

            if (";:".includes(lcChar)) {
                score.push(`${score.pop()?.replace(".", "")}.`);
                flattened.clear();
                pushNote(",");
            }
        } else {
            if (char === "*") {
                foundStar = true;
            }
        }

        if (char === "(") {
            isInsideNeume = true;
        }
    }

    return { score, clefLetter, clefOffset };
};

export const EBPlayButton: React.FC<{
    gabc: string;
    title: string;
    onlyIntone?: boolean;
    tone: string;
    tweak: number;
}> = (props) => {
    const { gabc, title, onlyIntone, tone, tweak } = props;

    const { settings } = useSettings();
    const transposeBy =
        tweak +
        settings.transpositions["1"] +
        (LIBER_TONES[tone]
            ? getTransposeByFromToneObject(LIBER_TONES[tone])
            : getTransposeByFromMode(tone.slice(0, 1)));

    const [playing, setPlaying] = useState(false);
    const playingIdRef = useRef<number>(0);

    useEffect(() => {}, []);

    const { score, clefLetter, clefOffset } = useMemo(() => {
        return getNotesFromGabc(gabc, onlyIntone ?? false);
    }, [gabc, onlyIntone]);

    const play = useCallback(() => {
        console.log(gabc, score);
        setPlaying(true);

        const ac = new AudioContext();
        // create empty buffer
        var buffer = ac.createBuffer(1, 1, 22050);
        var source = ac.createBufferSource();
        source.buffer = buffer;
        // connect to output (your speakers)
        source.connect(ac.destination);
        // play the file
        source.start(0);
        playingIdRef.current = playTone(
            ac,
            clefLetter,
            clefOffset,
            score,
            () => {
                setPlaying(false);
            },
            (count: number) => undefined,
            transposeBy,
            playingIdRef
        );
    }, [clefLetter, clefOffset, score, transposeBy]);

    const stop = useCallback(() => {
        playingIdRef.current++;
    }, []);

    return (
        <div style={{ paddingLeft: 4 }}>
            <div
                className={playing ? "new-button-playing" : "new-button"}
                onClick={playing ? stop : play}
            >
                <div className="play-icon">&#9658;</div>
                <div>{title}</div>
            </div>
        </div>
    );
};
